import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquare, BadgeCheck, PenLine, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import StarRating from "../components/StarRating";
import HelpfulButton from "../components/HelpfulButton";
import Reveal, { SectionLabel } from "../components/Reveal";

const inputCls =
  "rounded-xl border border-line bg-ink px-4 py-3 font-mono text-xs text-white outline-none transition-colors focus:border-signal";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-muted";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [firm, setFirm] = useState("all");
  const [rating, setRating] = useState("all");
  const [sort, setSort] = useState("newest");
  const [writing, setWriting] = useState(false);
  const [draft, setDraft] = useState({ firm_id: "", rating: 0, title: "", comment: "" });
  const [posting, setPosting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadReviews = () => api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});

  useEffect(() => {
    loadReviews().finally(() => setLoading(false));
    api.get("/firms").then(({ data }) => setFirms(data)).catch(() => {});
  }, []);

  const firmOptions = useMemo(() => firms.map((f) => [f.slug, f.name]), [firms]);

  const submitReview = async () => {
    if (!draft.firm_id) { toast.error("PICK A FIRM"); return; }
    if (!draft.rating) { toast.error("PICK A STAR RATING"); return; }
    if (draft.comment.trim().length < 5) { toast.error("COMMENT MUST BE AT LEAST 5 CHARACTERS"); return; }
    setPosting(true);
    try {
      await api.post(`/firms/${draft.firm_id}/reviews`, {
        rating: draft.rating,
        title: draft.title.trim(),
        comment: draft.comment.trim(),
      });
      setDraft({ firm_id: "", rating: 0, title: "", comment: "" });
      setWriting(false);
      toast.success("REVIEW PUBLISHED");
      await loadReviews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  const updateHelpful = (id, count, voted) =>
    setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, helpful_count: count, helpful_voted: voted } : r)));

  const filtered = useMemo(() => {
    let list = [...reviews];
    if (firm !== "all") list = list.filter((r) => r.firm_slug === firm);
    if (rating !== "all") list = list.filter((r) => r.rating === Number(rating));
    if (sort === "oldest") list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sort === "highest") list.sort((a, b) => b.rating - a.rating);
    else if (sort === "lowest") list.sort((a, b) => a.rating - b.rating);
    else if (sort === "helpful") list.sort((a, b) => (b.helpful_count || 0) - (a.helpful_count || 0));
    return list;
  }, [reviews, firm, rating, sort]);

  const avg = filtered.length
    ? (filtered.reduce((s, r) => s + r.rating, 0) / filtered.length).toFixed(1)
    : null;

  return (
    <div data-testid="reviews-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="scanlines-overlay" />
        <div className="absolute -left-24 top-0 h-64 w-64 rounded-full bg-[#8B5CF6]/15 blur-[130px]" />
        <div className="relative mx-auto max-w-6xl px-5 py-16 lg:px-8">
          <Reveal>
            <SectionLabel>Community Voice</SectionLabel>
            <h1 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
              Member <span className="text-gradient">reviews</span>
            </h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-muted">
              Every review from every firm, straight from club members. Verified badges mark traders with desk-checked payout proofs.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <Reveal>
          <div className="card-violet flex flex-wrap items-end gap-4 p-5" data-testid="reviews-filters">
            <div>
              <label className={labelCls}>Firm</label>
              <select value={firm} onChange={(e) => setFirm(e.target.value)} className={`${inputCls} mt-2`} data-testid="review-filter-firm">
                <option value="all">All firms</option>
                {firmOptions.map(([slug, name]) => <option key={slug} value={slug}>{name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Rating</label>
              <select value={rating} onChange={(e) => setRating(e.target.value)} className={`${inputCls} mt-2`} data-testid="review-filter-rating">
                <option value="all">All ratings</option>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Sort</label>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className={`${inputCls} mt-2`} data-testid="review-sort">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="helpful">Most helpful</option>
                <option value="highest">Highest rated</option>
                <option value="lowest">Lowest rated</option>
              </select>
            </div>
            <button
              onClick={() => (user ? setWriting((w) => !w) : navigate("/auth?next=/reviews"))}
              className="btn-gradient inline-flex items-center gap-2 rounded-xl px-5 py-3 font-mono text-[10px] font-700 uppercase tracking-[0.15em] text-white"
              data-testid="write-review-btn"
            >
              {writing ? <X size={13} /> : <PenLine size={13} />} {writing ? "Cancel" : "Write a Review"}
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted lg:ml-auto" data-testid="review-count">
              {filtered.length} review{filtered.length === 1 ? "" : "s"}{avg ? ` · avg ${avg}` : ""}
            </p>
          </div>
        </Reveal>

        {writing && user && (
          <Reveal>
            <div className="card-violet mt-6 p-6 sm:p-7" data-testid="write-review-form">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Share Your Experience"}</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Firm</label>
                  <select value={draft.firm_id} onChange={(e) => setDraft({ ...draft, firm_id: e.target.value })}
                    className={`${inputCls} mt-2 w-full`} data-testid="write-review-firm">
                    <option value="">Select firm…</option>
                    {firms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Your rating</label>
                  <div className="mt-3.5" data-testid="write-review-rating">
                    <StarRating value={draft.rating} onChange={(n) => setDraft({ ...draft, rating: n })} size={22} idPrefix="write-review" />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <label className={labelCls}>Title (optional)</label>
                <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  maxLength={120} placeholder="One line that sums it up"
                  className={`${inputCls} mt-2 w-full`} data-testid="write-review-title" />
              </div>
              <div className="mt-4">
                <label className={labelCls}>Review</label>
                <textarea value={draft.comment} onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
                  rows={4} maxLength={2000} placeholder="Payout speed, rules, support — what should traders know?"
                  className={`${inputCls} mt-2 w-full resize-none`} data-testid="write-review-comment" />
              </div>
              <button onClick={submitReview} disabled={posting}
                className="btn-beam btn-gradient mt-5 inline-flex items-center gap-2 px-6 py-3.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] text-white disabled:opacity-50"
                data-testid="write-review-submit">
                <PenLine size={13} /> {posting ? "Publishing…" : "Publish Review"}
              </button>
            </div>
          </Reveal>
        )}

        <div className="mt-8 space-y-4" data-testid="reviews-list">
          {loading ? (
            <p className="py-16 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-muted">Loading reviews…</p>
          ) : filtered.length === 0 ? (
            <div className="card-violet flex flex-col items-center gap-3 py-16 text-center">
              <MessageSquare size={26} className="text-signal" />
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted" data-testid="reviews-empty">
                {firm !== "all" ? "No reviews for this firm yet — be the first" : "No reviews match these filters"}
              </p>
              <button onClick={() => (user ? setWriting(true) : navigate("/auth?next=/reviews"))}
                className="mt-1 inline-flex items-center gap-2 rounded-full border border-signal/40 bg-signal/10 px-5 py-2.5 font-mono text-[10px] font-700 uppercase tracking-[0.15em] text-signal transition-colors hover:bg-signal/20"
                data-testid="empty-write-btn">
                <PenLine size={12} /> Write the first review
              </button>
            </div>
          ) : (
            filtered.map((r, i) => (
              <Reveal key={r.id} delay={Math.min(i, 6) * 0.05}>
                <article className="card-violet p-6 transition-colors duration-300 hover:border-signal/40" data-testid={`review-card-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                      <Link to={`/firms/${r.firm_slug}`} className="flex items-center gap-3" data-testid={`review-firm-${r.id}`}>
                        {r.firm_logo && (
                          <img src={r.firm_logo} alt={r.firm_name} className="h-10 w-10 rounded-xl border border-line bg-white object-contain p-1" loading="lazy" />
                        )}
                        <span className="font-display text-base font-800 text-white transition-colors hover:text-signal">{r.firm_name}</span>
                      </Link>
                      <StarRating value={r.rating} size={13} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted">{fmtDate(r.created_at)}</span>
                  </div>
                  {r.title && <p className="mt-4 font-display text-lg font-700 text-white">{r.title}</p>}
                  <p className="mt-2 text-sm leading-relaxed text-muted">{r.comment}</p>
                  <div className="mt-4 flex items-center gap-2.5 border-t border-line/60 pt-4">
                    <Link to={`/members/${r.user_id}`} className="flex items-center gap-2.5" data-testid={`review-author-${r.id}`}>
                      {r.picture ? (
                        <img src={r.picture} alt="" className="h-7 w-7 rounded-full" loading="lazy" />
                      ) : (
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-signal/15 font-display text-xs font-800 text-signal">
                          {(r.name || "?")[0].toUpperCase()}
                        </span>
                      )}
                      <span className="font-mono text-[11px] font-600 uppercase tracking-[0.1em] text-white transition-colors hover:text-signal">{r.name}</span>
                    </Link>
                    {r.verified_payout && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-profit/40 bg-profit/10 px-2.5 py-1 font-mono text-[8px] font-700 uppercase tracking-[0.12em] text-profit" data-testid={`review-verified-${r.id}`}>
                        <BadgeCheck size={10} /> Verified payout
                      </span>
                    )}
                    <span className="ml-auto">
                      <HelpfulButton review={r} onUpdate={updateHelpful} />
                    </span>
                  </div>
                </article>
              </Reveal>
            ))
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
